import { render, waitFor } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { expect } from 'chai';
import { getRoot, getItems, populateData, TestComponentsProps } from './testHelpers';

export const testRender = ({
  GenericCardGrid,
  ActionCardGrid,
  SelectionCardGrid,
  ContentUpdatableCardGrid
}: TestComponentsProps) => {
  it('initial render', async function () {
    const data = populateData(100);
    render(<GenericCardGrid data={data}></GenericCardGrid>);
    const items = await getItems('gridcell');
    expect(items.length).eq(25);
  });

  it('checking key type - string key', async () => {
    const data = populateData(3);
    render(<GenericCardGrid data={data}></GenericCardGrid>);
    const items = await getItems('gridcell');
    expect((items[0] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[1] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[2] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
  });

  it('checking key type - numeric key', async () => {
    const data = populateData(3, true);
    render(<GenericCardGrid data={data}></GenericCardGrid>);
    const items = await getItems('gridcell');
    expect((items[0] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[1] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[2] as HTMLElement).dataset['ojKeyType']).eq('number');
  });

  it('aria attributes - multiselectable and selected, selectionMode is none', async () => {
    const data = populateData(2);
    render(<SelectionCardGrid data={data}></SelectionCardGrid>);
    const items = await getItems('gridcell');
    const grid = await getRoot();

    expect((grid as HTMLElement).getAttribute('aria-multiselectable')).eq(null);
    expect((items[0] as HTMLElement).getAttribute('aria-selected')).eq(null);
    expect((items[1] as HTMLElement).getAttribute('aria-selected')).eq(null);
  });

  it('aria attributes - multiselectable and selected, selectionMode is single', async () => {
    const data = populateData(2);
    render(
      <SelectionCardGrid
        data={data}
        selectionMode={'single'}
        selectedKeys={new Set(['1'])}></SelectionCardGrid>
    );
    const items = await getItems('gridcell');
    const grid = await getRoot();

    expect((grid as HTMLElement).getAttribute('aria-multiselectable')).eq('false');
    expect((items[0] as HTMLElement).getAttribute('aria-selected')).eq('false');
    expect((items[1] as HTMLElement).getAttribute('aria-selected')).eq('true');
  });

  it('aria attributes - multiselectable and selected, selectionMode is multiple', async () => {
    const data = populateData(3);
    render(
      <SelectionCardGrid
        data={data}
        selectionMode={'multiple'}
        selectedKeys={new Set(['1', '2'])}></SelectionCardGrid>
    );
    const items = await getItems('gridcell');
    const grid = await getRoot();

    expect((grid as HTMLElement).getAttribute('aria-multiselectable')).eq('true');
    expect((items[0] as HTMLElement).getAttribute('aria-selected')).eq('false');
    expect((items[1] as HTMLElement).getAttribute('aria-selected')).eq('true');
    expect((items[2] as HTMLElement).getAttribute('aria-selected')).eq('true');
  });

  // skip this test in jest, only run it in karma mocha
  if (typeof jest === typeof undefined) {
    // because jest doesn't show grid layout, the number of focusable elements is not calculated correctly
    it('content focusBehavior - valid case', async () => {
      const data = populateData(3);
      render(<ActionCardGrid data={data}></ActionCardGrid>);
      const items = await getItems('gridcell');

      await userEvent.tab();

      await waitFor(() => {
        expect(items[0].matches(':focus-within')).to.be.true;
      });
    });

    it('content focusBehavior - invalid case', async () => {
      const data = populateData(3);
      render(<ActionCardGrid data={data} showButton={true}></ActionCardGrid>);
      const grid = await getRoot();

      await userEvent.tab();
      await waitFor(() => {
        expect(grid.matches(':focus')).to.be.true;
      });
    });
  }

  // JET-61732
  it('memoization - update memoized children', async () => {
    const data = populateData(3);
    const { queryByTestId } = render(
      <ContentUpdatableCardGrid data={data}></ContentUpdatableCardGrid>
    );

    const elem0 = queryByTestId(data[0].id) as HTMLElement;
    const elem1 = queryByTestId(data[1].id) as HTMLElement;
    const elem2 = queryByTestId(data[2].id) as HTMLElement;
    expect(elem0?.innerHTML, 'the 1st item initial count should be 0').eq('0');
    expect(elem1?.innerHTML, 'the 2nd item initial count should be 0').eq('0');
    expect(elem2?.innerHTML, 'the 3rd item initial count should be 0').eq('0');

    const button = queryByTestId('mybutton') as HTMLElement;
    await userEvent.click(button);

    expect(elem0?.innerHTML, 'the 1st item new count should be 1').eq('1');
    expect(elem1?.innerHTML, 'the 2nd item new count should be 1').eq('1');
    expect(elem2?.innerHTML, 'the 3rd item new count should be 1').eq('1');
  });
};
